import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Eye, EyeOff, Building2, CheckCircle2, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { US_STATES } from "@/lib/us-states";
import { useRedirectIfAuthed } from "@/hooks/useRedirectIfAuthed";

const STEPS = ["Account", "Pharmacy Details"] as const;

const PharmacyRegister = () => {
  useRedirectIfAuthed();
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    pharmacyName: "",
    licenseNumber: "",
    state: "",
    address: "",
    city: "",
    zipCode: "",
    phone: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateAccount = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.firstName.trim()) errs.firstName = "First name required";
    if (!formData.lastName.trim()) errs.lastName = "Last name required";
    if (!formData.email.trim()) errs.email = "Email required";
    if (!formData.password || formData.password.length < 6) errs.password = "Password must be at least 6 characters";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validatePharmacy = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.pharmacyName.trim()) errs.pharmacyName = "Pharmacy name required";
    if (!formData.state) errs.state = "State required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAccountStep = async () => {
    if (!validateAccount()) return;
    setIsLoading(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: { emailRedirectTo: `${window.location.origin}/pharmacy` },
    });

    if (signUpError) {
      toast({ title: "Registration failed", description: signUpError.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    const newUserId = signUpData.user?.id;
    if (!newUserId) {
      toast({ title: "Error", description: "Could not create your account.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    setUserId(newUserId);

    if (signUpData.session) {
      await supabase.from("profiles").update({
        first_name: formData.firstName,
        last_name: formData.lastName,
      }).eq("user_id", newUserId);
    }

    setIsLoading(false);
    setCurrentStep(1);
  };

  const handlePharmacyStep = async () => {
    if (!validatePharmacy() || !userId) return;
    setIsLoading(true);

    const { data: roleReqData } = await supabase.functions.invoke("create-role-request", {
      body: {
        user_id: userId,
        requested_role: "pharmacy",
        metadata: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          pharmacy_name: formData.pharmacyName,
          license_number: formData.licenseNumber,
          state: formData.state,
          address: formData.address,
          city: formData.city,
          zip_code: formData.zipCode,
          phone: formData.phone,
        },
      },
    });
    const requestId = roleReqData?.id || crypto.randomUUID();

    // Notify admins via email
    const { data: adminEmails } = await supabase.rpc("get_user_emails");
    const { data: adminRoles } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
    const adminUserIds = new Set((adminRoles || []).map((r) => r.user_id));
    const admins = (adminEmails || []).filter((u) => adminUserIds.has(u.user_id));

    for (const admin of admins) {
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "new-role-request",
          recipientEmail: admin.email,
          idempotencyKey: `role-request-${requestId}-${admin.user_id}`,
          templateData: {
            applicantName: `${formData.firstName} ${formData.lastName}`,
            applicantEmail: formData.email,
            requestedRole: "pharmacy",
          },
        },
      });
    }

    setIsLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border/50 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="text-primary" size={32} />
            </div>
            <h2 className="font-display text-2xl text-foreground">Application Submitted!</h2>
            <p className="text-muted-foreground">
              Your pharmacy partner application has been submitted. An admin will review your details and approve your account shortly.
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-4">Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-foreground">Pharmacy Partner Registration</h1>
            <p className="text-muted-foreground text-sm">Join our compounding pharmacy network</p>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((step, i) => (
              <span key={step} className={`text-xs font-medium ${i <= currentStep ? "text-primary" : "text-muted-foreground"}`}>
                {step}
              </span>
            ))}
          </div>
          <Progress value={((currentStep + 1) / STEPS.length) * 100} className="h-2" />
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardContent className="pt-6 space-y-4">
            {currentStep === 0 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input id="firstName" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} />
                    {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input id="lastName" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} />
                    {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => updateField("password", e.target.value)} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>
                <Button onClick={handleAccountStep} disabled={isLoading} className="w-full gap-2">
                  {isLoading ? "Creating Account…" : <>Continue <ArrowRight size={16} /></>}
                </Button>
              </>
            )}

            {currentStep === 1 && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="pharmacyName">Pharmacy Name</Label>
                  <Input id="pharmacyName" value={formData.pharmacyName} onChange={(e) => updateField("pharmacyName", e.target.value)} />
                  {errors.pharmacyName && <p className="text-xs text-destructive">{errors.pharmacyName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="licenseNumber">Pharmacy License Number (optional)</Label>
                  <Input id="licenseNumber" value={formData.licenseNumber} onChange={(e) => updateField("licenseNumber", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>State</Label>
                    <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
                      <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={formData.city} onChange={(e) => updateField("city", e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={formData.address} onChange={(e) => updateField("address", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="zipCode">ZIP Code</Label>
                    <Input id="zipCode" value={formData.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} />
                  </div>
                </div>
                <Button onClick={handlePharmacyStep} disabled={isLoading} className="w-full gap-2">
                  {isLoading ? "Submitting…" : <>Submit Application <CheckCircle2 size={16} /></>}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default PharmacyRegister;
